/**
 * Types for the keyword-pin and pinned-message system in the Focused Lead View.
 *
 * See `./README.md` for the contract. These types are the public surface for
 * `useKeywordPins`, `usePinnedMessages`, the pin editor UI, and anything that
 * reads the seeded defaults / library presets.
 */

/**
 * When the pin's prompt fires.
 *
 * - `before_call` — auto-sent before the call is dialed (pre-call prep).
 * - `on_dial`     — auto-sent the moment the dial button is pressed.
 * - `starter`     — tap-to-send from the pin rail. No auto behavior.
 */
export type PinTrigger = 'before_call' | 'on_dial' | 'starter';

/**
 * A hex color value chosen from `PIN_COLORS`. Stored as the hex string itself
 * (e.g. `#EA6A2C`), not a named token — the palette is fixed and UI renders
 * the color directly.
 */
export type PinColor = string;

/**
 * A user-defined (or built-in) prompt shortcut. Typing a message that starts
 * with `keyword` triggers the pin: the AI receives `prompt` instead of the
 * raw message, and the reply is auto-pinned on the right rail.
 */
export type KeywordPin = {
	id: string;
	label: string;
	keyword: string;
	color: PinColor;
	prompt: string;
	trigger: PinTrigger;
	enabled: boolean;
	/** Seeded default. Label/keyword are not user-editable and delete is disabled. */
	builtin?: boolean;
	/**
	 * Set the moment the user touches a built-in pin via the editor. When
	 * `true`, the rehydration step in `useKeywordPins` keeps the stored values
	 * verbatim instead of overwriting them with the latest `DEFAULT_KEYWORD_PINS`.
	 * Untouched built-ins inherit code-side default updates automatically. The
	 * field is meaningless on non-builtin pins.
	 */
	userEdited?: boolean;
};

/**
 * Shape accepted by `useKeywordPins().create()`. The hook assigns `id` and
 * forces `builtin` to `false` (or omits it) — callers cannot smuggle in a
 * `builtin: true` via creation.
 */
export type NewKeywordPin = {
	label: string;
	keyword: string;
	color: PinColor;
	prompt: string;
	trigger: PinTrigger;
	enabled?: boolean;
};

/**
 * A pickable starter shown in the pin editor's "Library" section. Selecting
 * one pre-fills the editor form; it does not create a pin on its own.
 */
export type LibraryPreset = {
	label: string;
	keyword: string;
	color: PinColor;
	prompt: string;
};

/**
 * Fixed palette for pin colors. Order here is the order the UI shows swatches.
 */
export const PIN_COLORS: ReadonlyArray<{ name: string; value: string }> = [
	{ name: 'Orange', value: '#EA6A2C' },
	{ name: 'Blue', value: '#3B82F6' },
	{ name: 'Green', value: '#10A37F' },
	{ name: 'Purple', value: '#8B5CF6' },
	{ name: 'Pink', value: '#EC4899' },
	{ name: 'Slate', value: '#64748B' },
];
